import type { ReactNode } from 'react';
import { Outlet, useRouter } from '@tanstack/react-router';
import { lazyRoute } from '@/services/router/prefetch';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import type { WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';
import { Navigate } from '@/utils/navigate';
import { LastWorkspaceResolver, LazyPage } from './-route-layouts';

const SubscriptionCenterIndexPage = lazyRoute(
  '/workspaces/:workspaceId/settings/subscription_center',
  () => import('@/pages/workspaces/settings/subscription-center/index/index.page'),
);
const SubscriptionCenterSettingsPage = lazyRoute(
  '/workspaces/:workspaceId/settings/subscription_center/settings',
  () => import('@/pages/workspaces/settings/subscription-center/settings/index.page'),
);
const SubscriptionCenterSetPreferencesPage = lazyRoute(
  '/workspaces/:workspaceId/settings/subscription_center/topics/set_preferences',
  () => import('@/pages/workspaces/settings/subscription-center/set-preferences/index.page'),
);
const SubscriptionCenterBrandingPage = lazyRoute(
  '/workspaces/:workspaceId/settings/subscription_center/branding/edit',
  () => import('@/pages/workspaces/settings/subscription-center/branding/index.page'),
);
const SubscriptionCenterLocalizationPage = lazyRoute(
  '/workspaces/:workspaceId/settings/subscription_center/localization/edit',
  () => import('@/pages/workspaces/settings/subscription-center/localization/index.page'),
);

type SubscriptionCenterGateProps = {
  children: ReactNode;
};

// The workspace-settings parent every one of these routes sits under admits a read-access
// masquerade regardless of the workspace permission; the pages gate their own controls on
// `journeys/subscription_center/edit` and `/delete`.
function SubscriptionCenterGate({ children }: SubscriptionCenterGateProps) {
  const router = useRouter();

  return (
    <ProtectedRoute
      permission="journeys/workspace_settings/view"
      errorMessage="You do not have permission to view workspace settings. If you'd like access, let an admin know."
      allowReadOnlyMasqAccess={true}
      redirectTo={(params) =>
        router.buildLocation({
          to: '/workspaces/$workspaceId/settings',
          params: { workspaceId: params.workspaceId ?? 'last' },
        }).href
      }
    >
      {children}
    </ProtectedRoute>
  );
}

/**
 * The subscription center route set (FPS-2356): the tabbed Subscriptions / Settings pages, the
 * bulk preference table, the branding editor and the localization editor all render through this
 * layout, so the permission check lives here once.
 */
export function SubscriptionCenterLayout() {
  return (
    <LastWorkspaceResolver>
      <SubscriptionCenterGate>
        <Outlet />
      </SubscriptionCenterGate>
    </LastWorkspaceResolver>
  );
}

export function SubscriptionCenterIndexRoute() {
  return (
    <LazyPage>
      <SubscriptionCenterIndexPage />
    </LazyPage>
  );
}

export function SubscriptionCenterSettingsRoute() {
  return (
    <LazyPage>
      <SubscriptionCenterSettingsPage />
    </LazyPage>
  );
}

export function SubscriptionCenterSetPreferencesRoute() {
  return (
    <LazyPage>
      <SubscriptionCenterSetPreferencesPage />
    </LazyPage>
  );
}

export function SubscriptionCenterBrandingRoute() {
  return (
    <LazyPage>
      <SubscriptionCenterBrandingPage />
    </LazyPage>
  );
}

export function SubscriptionCenterLocalizationRoute() {
  return (
    <LazyPage>
      <SubscriptionCenterLocalizationPage />
    </LazyPage>
  );
}

type SubscriptionCenterTopicsRouteProps = {
  workspaceId: WorkspaceIdRouteParamInput;
};

/**
 * `/settings/subscription_center/topics` has no page of its own; it sends the reader to the
 * Subscriptions tab. The redirect replaces, so the bare `topics` URL never lands in history.
 */
export function SubscriptionCenterTopicsRoute({ workspaceId }: SubscriptionCenterTopicsRouteProps) {
  const router = useRouter();
  const target = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/subscription_center',
    params: { workspaceId },
  }).href;

  return <Navigate to={target} replace={true} />;
}
